import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { Password } from "primereact/password";
import { Toast } from "primereact/toast";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { RootState, AppDispatch } from "../Redux/store";
import { resetUser } from "../Redux/reducer/userSlice";
import "./PageLayout.css";
import "./Account.css";

// Usa i nuovi hooks e services
import { useAuth, useErrorHandler } from "../hooks/useServices";
import { validatePassword } from "../lib/validators";

const Account: React.FC = () => {
  const [changePasswordVisible, setChangePasswordVisible] = useState<boolean>(false);
  const [unlockUserVisible, setUnlockUserVisible] = useState<boolean>(false);
  const [auditLogsVisible, setAuditLogsVisible] = useState<boolean>(false);
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [unlockCodiceFiscale, setUnlockCodiceFiscale] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUnlocking, setIsUnlocking] = useState<boolean>(false);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [isLoadingAudit, setIsLoadingAudit] = useState<boolean>(false);
  const [auditSearchCF, setAuditSearchCF] = useState<string>("");
  const [auditStats, setAuditStats] = useState<any>(null);
  const [auditFilterAction, setAuditFilterAction] = useState<string>("");
  const [auditFilterDateFrom, setAuditFilterDateFrom] = useState<string>("");
  const [auditFilterDateTo, setAuditFilterDateTo] = useState<string>("");
  const [auditFilterSuccess, setAuditFilterSuccess] = useState<string>("");

  // Stato Audit Ripubblicazioni
  const [republishAuditVisible, setRepublishAuditVisible] = useState<boolean>(false);
  const [republishAuditLogs, setRepublishAuditLogs] = useState<any[]>([]);
  const [republishAuditStats, setRepublishAuditStats] = useState<any>(null);
  const [isLoadingRepublishAudit, setIsLoadingRepublishAudit] = useState<boolean>(false);
  const [republishSearchCF, setRepublishSearchCF] = useState<string>("");
  const [republishFilterStatus, setRepublishFilterStatus] = useState<string>("");
  const [republishFilterDateFrom, setRepublishFilterDateFrom] = useState<string>("");
  const [republishFilterDateTo, setRepublishFilterDateTo] = useState<string>("");

  const toast = useRef<Toast>(null);

  // Lista amministratori che possono sbloccare utenti
  const ADMIN_USERS = ["FRSRFL72R25H282U", "GRRLCU88P05H501J"];

  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.user);

  // Usa i nuovi hooks
  const { logout, changePassword, getPatientInfo } = useAuth();
  useErrorHandler(toast); // Configura error handler con toast

  // Fallback: se patientInfo è incompleto, prova UNA sola volta a ricaricarlo
  // (il guard via ref evita loop quando la chiamata fallisce con 401).
  const patientInfoFetchAttempted = useRef(false);
  useEffect(() => {
    if (patientInfoFetchAttempted.current) return;
    const needsLoad =
      user.isLoggedIn &&
      user.codiceFiscale &&
      (!user.patientInfo ||
        !user.patientInfo.firstName ||
        !user.patientInfo.lastName);
    if (needsLoad) {
      patientInfoFetchAttempted.current = true;
      getPatientInfo(user.codiceFiscale).catch(() => { /* logged dall'handler */ });
    }
  }, [user.isLoggedIn, user.codiceFiscale, user.patientInfo, getPatientInfo]);

  const handleLogout = async () => {
    try {
      await logout();
      dispatch(resetUser());
      toast.current?.show({
        severity: "success",
        summary: "Logout effettuato",
        detail: "Sei stato disconnesso con successo.",
        life: 3000,
      });
      // Forza il reload della pagina per tornare al login
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } catch (error) {
      // L'error handler gestirà automaticamente
    }
  };


  const handleChangePasswordSubmit = async () => {
    // Verifica che il codice fiscale (username) sia disponibile
    if (!user.codiceFiscale) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Informazioni dell'utente mancanti. Riprova dopo il login.",
        life: 3000,
      });
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Compila tutti i campi richiesti.",
        life: 3000,
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "La nuova password e la conferma non coincidono.",
        life: 3000,
      });
      return;
    }

    // Usa il validatore centralizzato
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      toast.current?.show({
        severity: "error",
        summary: "Password non valida",
        detail: validation.errors.join(" "),
        life: 5000,
      });
      return;
    }

    setIsLoading(true);
    try {
      // Usa il nuovo service
      const success = await changePassword(
        user.codiceFiscale,
        currentPassword,
        newPassword
      );

      if (success) {
        toast.current?.show({
          severity: "success",
          summary: "Successo",
          detail: "Password aggiornata con successo.",
          life: 3000,
        });
        
        setChangePasswordVisible(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (error: any) {
      // Gestione specifica per password errata
      if (error?.message?.includes("Incorrect password")) {
        toast.current?.show({
          severity: "error",
          summary: "Errore",
          detail: "La password attuale inserita non è corretta.",
          life: 3000,
        });
      }
      // Altri errori sono gestiti da ErrorHandler
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnlockUser = async () => {
    if (!unlockCodiceFiscale || unlockCodiceFiscale.trim().length === 0) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: "Inserisci il codice fiscale dell'utente da sbloccare.",
        life: 3000,
      });
      return;
    }

    setIsUnlocking(true);
    try {
      // Usa l'URL di base dell'applicazione (stessa origine del frontend)
      const baseURL = window.location.origin;

      const response = await fetch(`${baseURL}/api/Account/unlockUser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userName: unlockCodiceFiscale.trim().toUpperCase()
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Costruisci messaggio dettagliato per l'amministratore
        let detailMessage = data.Messaggio || data.messaggio || "Utente sbloccato con successo.";

        // Aggiungi informazioni dettagliate se disponibili
        if (data.accessFailedCount !== undefined || data.lockoutEnd || data.wasRecentlyLocked) {
          detailMessage += "\n\n📊 Dettagli:";

          if (data.isLockedOut === false) {
            detailMessage += "\n✅ Stato: Non bloccato";
          }

          if (data.accessFailedCount !== undefined) {
            detailMessage += `\n🔢 Tentativi falliti resettati: ${data.accessFailedCount === 0 ? 'Sì' : 'No'}`;
          }

          if (data.lockoutEnd) {
            const lockoutDate = new Date(data.lockoutEnd);
            detailMessage += `\n⏰ Ultimo lockout: ${lockoutDate.toLocaleString('it-IT')}`;
          }

          if (data.wasRecentlyLocked) {
            detailMessage += "\n⚠️ L'utente è stato recentemente bloccato";
          }
        }

        toast.current?.show({
          severity: "success",
          summary: "Operazione Completata",
          detail: detailMessage,
          life: 8000,
        });

        setUnlockUserVisible(false);
        setUnlockCodiceFiscale("");
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Errore",
          detail: data.Messaggio || data.messaggio || "Impossibile sbloccare l'utente.",
          life: 5000,
        });
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: error.message || "Errore durante lo sblocco dell'utente.",
        life: 5000,
      });
    } finally {
      setIsUnlocking(false);
    }
  };

  const isAdmin = () => {
    return ADMIN_USERS.includes(user.codiceFiscale?.toUpperCase() || "");
  };

  const loadAuditLogs = async (userName?: string) => {
    setIsLoadingAudit(true);
    try {
      const config = await import('../services/ConfigService');
      const apiUrl = config.default.getApiBaseUrl();

      let url = `${apiUrl}Account/auditLogs?pageSize=100&lastDays=30`;
      if (userName && userName.trim()) {
        url += `&userName=${encodeURIComponent(userName.trim())}`;
      }
      if (auditFilterAction) {
        url += `&action=${encodeURIComponent(auditFilterAction)}`;
      }
      if (auditFilterSuccess !== "") {
        url += `&success=${auditFilterSuccess}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Errore durante il caricamento degli audit log');
      }

      const data = await response.json();
      let logs = data.Logs || data.logs || [];

      // Filtraggio date lato client
      if (auditFilterDateFrom || auditFilterDateTo) {
        logs = logs.filter((log: any) => {
          const logDate = new Date(log.timestamp);
          const dateFrom = auditFilterDateFrom ? new Date(auditFilterDateFrom) : null;
          const dateTo = auditFilterDateTo ? new Date(auditFilterDateTo + 'T23:59:59') : null;

          if (dateFrom && logDate < dateFrom) return false;
          if (dateTo && logDate > dateTo) return false;
          return true;
        });
      }

      setAuditLogs(logs);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Errore',
        detail: error.message || 'Impossibile caricare gli audit log.',
        life: 5000,
      });
    } finally {
      setIsLoadingAudit(false);
    }
  };

  const loadAuditStats = async () => {
    try {
      const config = await import('../services/ConfigService');
      const apiUrl = config.default.getApiBaseUrl();

      const response = await fetch(`${apiUrl}Account/auditLogs/stats?lastDays=7`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Errore durante il caricamento delle statistiche');
      }

      const data = await response.json();
      setAuditStats(data);
    } catch (error: any) {
      console.error('Errore caricamento statistiche:', error);
    }
  };

  const handleOpenAuditLogs = () => {
    setAuditLogsVisible(true);
    loadAuditLogs();
    loadAuditStats();
  };

  const handleSearchAuditLogs = () => {
    loadAuditLogs(auditSearchCF);
  };

  const handleResetFilters = () => {
    setAuditSearchCF("");
    setAuditFilterAction("");
    setAuditFilterDateFrom("");
    setAuditFilterDateTo("");
    setAuditFilterSuccess("");
    loadAuditLogs();
  };

  const handleQuickFilterAccountLocked = () => {
    if (auditFilterAction === 'AccountLocked') {
      // Se già filtrato, rimuovi il filtro
      setAuditFilterAction("");
      loadAuditLogs();
    } else {
      // Filtra su AccountLocked
      setAuditFilterAction("AccountLocked");
      // Ricarica con il nuovo filtro
      const searchCF = auditSearchCF && auditSearchCF.trim() ? auditSearchCF : undefined;
      loadAuditLogs(searchCF);
    }
  };

  // === Audit Ripubblicazioni ===
  const loadRepublishAudit = async (searchUserName?: string) => {
    setIsLoadingRepublishAudit(true);
    try {
      const config = await import('../services/ConfigService');
      const apiUrl = config.default.getApiBaseUrl();

      let url = `${apiUrl}Document/republish-audit?pageSize=100&lastDays=90`;
      if (searchUserName && searchUserName.trim()) {
        url += `&userName=${encodeURIComponent(searchUserName.trim())}`;
      }
      if (republishFilterStatus) {
        url += `&status=${encodeURIComponent(republishFilterStatus)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Errore durante il caricamento dell\'audit ripubblicazioni');
      }

      const data = await response.json();
      let logs = data.logs || [];

      // Filtraggio date lato client
      if (republishFilterDateFrom || republishFilterDateTo) {
        logs = logs.filter((log: any) => {
          const logDate = new Date(log.createdAt);
          const dateFrom = republishFilterDateFrom ? new Date(republishFilterDateFrom) : null;
          const dateTo = republishFilterDateTo ? new Date(republishFilterDateTo + 'T23:59:59') : null;
          if (dateFrom && logDate < dateFrom) return false;
          if (dateTo && logDate > dateTo) return false;
          return true;
        });
      }

      setRepublishAuditLogs(logs);
      setRepublishAuditStats(data.stats || null);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Errore',
        detail: error.message || 'Impossibile caricare l\'audit ripubblicazioni.',
        life: 5000,
      });
    } finally {
      setIsLoadingRepublishAudit(false);
    }
  };

  const handleOpenRepublishAudit = () => {
    setRepublishAuditVisible(true);
    loadRepublishAudit();
  };

  const handleSearchRepublishAudit = () => {
    loadRepublishAudit(republishSearchCF);
  };

  const handleResetRepublishFilters = () => {
    setRepublishSearchCF("");
    setRepublishFilterStatus("");
    setRepublishFilterDateFrom("");
    setRepublishFilterDateTo("");
    loadRepublishAudit();
  };

  const getRepublishStatusBadge = (status: string) => {
    switch (status) {
      case 'Confermato':
        return <span style={{ backgroundColor: '#c8e6c9', color: 'var(--primary-dark, #2e7d32)', padding: '3px 10px', borderRadius: '12px', fontSize: '0.85em', fontWeight: 'bold' }}>Confermato</span>;
      case 'Scaduto':
        return <span style={{ backgroundColor: '#ffcdd2', color: '#c62828', padding: '3px 10px', borderRadius: '12px', fontSize: '0.85em', fontWeight: 'bold' }}>Scaduto</span>;
      case 'In attesa':
        return <span style={{ backgroundColor: '#fff9c4', color: '#f57f17', padding: '3px 10px', borderRadius: '12px', fontSize: '0.85em', fontWeight: 'bold' }}>In attesa</span>;
      default:
        return <span>{status}</span>;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionLabel = (action: string) => {
    const labels: { [key: string]: string } = {
      'Login': 'Login',
      'LoginFailed': 'Login Fallito',
      'Logout': 'Logout',
      'PasswordChange': 'Cambio Password',
      'PasswordReset': 'Reset Password',
      'EmailConfirmation': 'Conferma Email',
      'AccountUnlocked': 'Account Sbloccato',
      'AccountLocked': 'Account Bloccato',
      'RegistrationAttempt': 'Tentativo Registrazione',
      'RegistrationCompleted': 'Registrazione Completata'
    };
    return labels[action] || action;
  };

  const getWelcomeMessage = () => {
    const genderMap: { [key: string]: string } = {
      "1": "Benvenuto",
      "0": "Benvenuta",
    };
    const welcomeMessage =
      genderMap[user.patientInfo?.sexId?.trim() || ""] || "Benvenuto";
    return (
      <span className="welcome-message">
        {welcomeMessage} {user.patientInfo?.firstName || ""} {user.patientInfo?.lastName || ""}
      </span>
    );
  };

  return (
    <div className="page-container">
      <Toast ref={toast} />

      <div className="page-content">
        <div className="content-grid">
          <Card className="page-card">
        <h2>Il mio Account</h2>
        
        <div className="user-info">
          <div className="info-header">
            {getWelcomeMessage()}
          </div>
          
          <div className="info-grid">
            <div className="info-item">
              <label>Nome:</label>
              <span>{user.patientInfo?.firstName || "N/D"}</span>
            </div>
            
            <div className="info-item">
              <label>Cognome:</label>
              <span>{user.patientInfo?.lastName || "N/D"}</span>
            </div>
            
            <div className="info-item">
              <label>Codice Fiscale:</label>
              <span>{user.codiceFiscale || "N/D"}</span>
            </div>
            
            <div className="info-item">
              <label>Email:</label>
              <span>{user.patientInfo?.email || user.email || "N/D"}</span>
            </div>
            
            {user.patientInfo?.birthDate && (
              <div className="info-item">
                <label>Data di nascita:</label>
                <span>
                  {new Date(user.patientInfo.birthDate).toLocaleDateString("it-IT")}
                </span>
              </div>
            )}
            
            {user.patientInfo?.phone && (
              <div className="info-item">
                <label>Telefono:</label>
                <span>{user.patientInfo.phone}</span>
              </div>
            )}
            
            {user.patientInfo?.mobilePhone && (
              <div className="info-item">
                <label>Cellulare:</label>
                <span>{user.patientInfo.mobilePhone}</span>
              </div>
            )}
            
            <div className="info-item">
              <label>Email verificata:</label>
              <span>
                {user.emailConfirmed ? (
                  <><i className="pi pi-check-circle" style={{ color: 'green' }}></i> Sì</>
                ) : (
                  <><i className="pi pi-times-circle" style={{ color: 'red' }}></i> No</>
                )}
              </span>
            </div>
            
            {user.lastLogin && (
              <div className="info-item">
                <label>Ultimo accesso:</label>
                <span>{new Date(user.lastLogin).toLocaleString("it-IT")}</span>
              </div>
            )}
            
            {user.patientInfo?.birthPlace && (
              <div className="info-item">
                <label>Luogo di nascita:</label>
                <span>{user.patientInfo.birthPlace}</span>
              </div>
            )}
          </div>
        </div>

        <div className="account-actions">
          <Button
            label="Cambia Password"
            icon="pi pi-lock"
            onClick={() => setChangePasswordVisible(true)}
            className="p-button-primary"
          />

          {isAdmin() && (
            <>
              <Button
                label="Sblocca Utente"
                icon="pi pi-unlock"
                onClick={() => setUnlockUserVisible(true)}
                className="p-button-warning"
              />
              <Button
                label="Visualizza Audit Log"
                icon="pi pi-list"
                onClick={handleOpenAuditLogs}
                className="p-button-info"
              />
              <Button
                label="Audit Ripubblicazioni"
                icon="pi pi-refresh"
                onClick={handleOpenRepublishAudit}
                className="p-button-help"
              />
            </>
          )}

          <Button
            label="Logout"
            icon="pi pi-sign-out"
            onClick={handleLogout}
            className="p-button-danger"
          />
        </div>

        <div className="additional-info">
          <h3>Informazioni sull'account</h3>
          <ul>
            <li>
              <strong>Ultimo accesso:</strong>{" "}
              {user.lastLogin
                ? new Date(user.lastLogin).toLocaleString("it-IT")
                : "N/D"}
            </li>
            <li>
              <strong>Account verificato:</strong>{" "}
              {user.emailConfirmed ? (
                <span className="verified">
                  <i className="pi pi-check-circle"></i> Sì
                </span>
              ) : (
                <span className="not-verified">
                  <i className="pi pi-times-circle"></i> No
                </span>
              )}
            </li>
            <li>
              <strong>Tipo account:</strong> Paziente
            </li>
          </ul>
        </div>

      {/* Dialog Cambio Password */}
      <Dialog
        header="Cambia Password"
        visible={changePasswordVisible}
        style={{ width: "450px" }}
        onHide={() => {
          if (!isLoading) {
            setChangePasswordVisible(false);
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
          }
        }}
        footer={
          <div>
            <Button
              label="Annulla"
              icon="pi pi-times"
              onClick={() => setChangePasswordVisible(false)}
              className="p-button-text"
              disabled={isLoading}
            />
            <Button
              label="Conferma"
              icon="pi pi-check"
              onClick={handleChangePasswordSubmit}
              loading={isLoading}
            />
          </div>
        }
      >
        <div className="change-password-form">
          <div className="input-group">
            <FloatLabel>
              <Password
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                toggleMask
                feedback={false}
                disabled={isLoading}
                onKeyPress={(e) => e.key === "Enter" && handleChangePasswordSubmit()}
              />
              <label htmlFor="currentPassword">Password Attuale</label>
            </FloatLabel>
          </div>

          <div className="input-group">
            <FloatLabel>
              <Password
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                toggleMask
                disabled={isLoading}
                onKeyPress={(e) => e.key === "Enter" && handleChangePasswordSubmit()}
              />
              <label htmlFor="newPassword">Nuova Password</label>
            </FloatLabel>
          </div>

          <div className="input-group">
            <FloatLabel>
              <Password
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                toggleMask
                feedback={false}
                disabled={isLoading}
                onKeyPress={(e) => e.key === "Enter" && handleChangePasswordSubmit()}
              />
              <label htmlFor="confirmPassword">Conferma Nuova Password</label>
            </FloatLabel>
          </div>

          <div className="password-requirements">
            <p className="requirements-title">La password deve contenere:</p>
            <ul>
              <li className={newPassword.length >= 8 ? "valid" : ""}>
                <i className={`pi ${newPassword.length >= 8 ? "pi-check" : "pi-times"}`}></i>
                Almeno 8 caratteri
              </li>
              <li className={/[a-z]/.test(newPassword) ? "valid" : ""}>
                <i className={`pi ${/[a-z]/.test(newPassword) ? "pi-check" : "pi-times"}`}></i>
                Una lettera minuscola
              </li>
              <li className={/[A-Z]/.test(newPassword) ? "valid" : ""}>
                <i className={`pi ${/[A-Z]/.test(newPassword) ? "pi-check" : "pi-times"}`}></i>
                Una lettera maiuscola
              </li>
              <li className={/\d/.test(newPassword) ? "valid" : ""}>
                <i className={`pi ${/\d/.test(newPassword) ? "pi-check" : "pi-times"}`}></i>
                Un numero
              </li>
              <li className={/[^A-Za-z0-9]/.test(newPassword) ? "valid" : ""}>
                <i className={`pi ${/[^A-Za-z0-9]/.test(newPassword) ? "pi-check" : "pi-times"}`}></i>
                Un carattere speciale
              </li>
            </ul>
          </div>
        </div>
      </Dialog>

      {/* Dialog Sblocca Utente */}
      <Dialog
        header="Sblocca Utente"
        visible={unlockUserVisible}
        style={{ width: "450px" }}
        onHide={() => {
          if (!isUnlocking) {
            setUnlockUserVisible(false);
            setUnlockCodiceFiscale("");
          }
        }}
        footer={
          <div>
            <Button
              label="Annulla"
              icon="pi pi-times"
              onClick={() => {
                setUnlockUserVisible(false);
                setUnlockCodiceFiscale("");
              }}
              className="p-button-text"
              disabled={isUnlocking}
            />
            <Button
              label="Sblocca"
              icon="pi pi-unlock"
              onClick={handleUnlockUser}
              loading={isUnlocking}
              className="p-button-warning"
            />
          </div>
        }
      >
        <div className="unlock-user-form">
          <p>
            Inserisci il codice fiscale dell'utente che desideri sbloccare.
            Questa operazione ripristinerà l'accesso per un utente bloccato
            per troppi tentativi di login falliti.
          </p>

          <div className="input-group" style={{ marginTop: '20px' }}>
            <FloatLabel>
              <InputText
                id="unlockCodiceFiscale"
                value={unlockCodiceFiscale}
                onChange={(e) => setUnlockCodiceFiscale(e.target.value.toUpperCase())}
                maxLength={16}
                disabled={isUnlocking}
                onKeyPress={(e) => e.key === "Enter" && handleUnlockUser()}
                style={{ width: '100%' }}
              />
              <label htmlFor="unlockCodiceFiscale">Codice Fiscale</label>
            </FloatLabel>
          </div>

          <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
            <p style={{ margin: 0, fontSize: '0.9em', color: '#856404' }}>
              <i className="pi pi-info-circle" style={{ marginRight: '5px' }}></i>
              <strong>Nota:</strong> Assicurati di inserire il codice fiscale corretto.
              L'operazione resetterà il contatore dei tentativi falliti e rimuoverà
              il blocco temporaneo dall'account.
            </p>
          </div>
        </div>
      </Dialog>

      {/* Dialog Audit Log */}
      <Dialog
        header="Audit Log Attività Utenti"
        visible={auditLogsVisible}
        style={{ width: "90vw", maxWidth: "1200px" }}
        onHide={() => {
          setAuditLogsVisible(false);
          setAuditSearchCF("");
          setAuditFilterAction("");
          setAuditFilterDateFrom("");
          setAuditFilterDateTo("");
          setAuditFilterSuccess("");
          setAuditLogs([]);
          setAuditStats(null);
        }}
        maximizable
      >
        <div className="audit-log-container">
          {/* Statistiche */}
          {auditStats && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '10px',
              marginBottom: '20px'
            }}>
              <Card style={{ padding: '10px', backgroundColor: '#e3f2fd' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>
                    {auditStats.totalEvents || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#555' }}>Eventi Totali (7gg)</div>
                </div>
              </Card>
              <Card style={{ padding: '10px', backgroundColor: '#f3e5f5' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7b1fa2' }}>
                    {auditStats.failedLogins || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#555' }}>Login Falliti</div>
                </div>
              </Card>
              <Card
                style={{
                  padding: '10px',
                  backgroundColor: auditFilterAction === 'AccountLocked' ? '#ffe0b2' : '#fff3e0',
                  cursor: 'pointer',
                  border: auditFilterAction === 'AccountLocked' ? '2px solid #f57c00' : 'none',
                  transition: 'all 0.3s ease'
                }}
                onClick={handleQuickFilterAccountLocked}
              >
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f57c00' }}>
                    {auditStats.accountsLocked || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#555' }}>
                    Account Bloccati {auditFilterAction === 'AccountLocked' && '🔍'}
                  </div>
                  <div style={{ fontSize: '10px', color: '#999', marginTop: '4px', fontStyle: 'italic' }}>
                    {auditFilterAction === 'AccountLocked' ? 'Clicca per rimuovere filtro' : 'Clicca per filtrare'}
                  </div>
                </div>
              </Card>
              <Card style={{ padding: '10px', backgroundColor: '#e8f5e9' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#388e3c' }}>
                    {auditStats.newRegistrations || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#555' }}>Nuove Registrazioni</div>
                </div>
              </Card>
            </div>
          )}

          {/* Filtri */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
              <div>
                <FloatLabel>
                  <InputText
                    id="auditSearchCF"
                    value={auditSearchCF}
                    onChange={(e) => setAuditSearchCF(e.target.value.toUpperCase())}
                    maxLength={16}
                    style={{ width: '100%' }}
                    onKeyPress={(e) => e.key === "Enter" && handleSearchAuditLogs()}
                  />
                  <label htmlFor="auditSearchCF">Codice Fiscale</label>
                </FloatLabel>
              </div>

              <div>
                <FloatLabel>
                  <select
                    id="auditFilterAction"
                    value={auditFilterAction}
                    onChange={(e) => setAuditFilterAction(e.target.value)}
                    style={{ width: '100%', padding: '11px', border: '1px solid #ced4da', borderRadius: '4px' }}
                  >
                    <option value="">Tutti gli eventi</option>
                    <option value="Login">Login</option>
                    <option value="LoginFailed">Login Fallito</option>
                    <option value="Logout">Logout</option>
                    <option value="PasswordChange">Cambio Password</option>
                    <option value="PasswordReset">Reset Password</option>
                    <option value="EmailConfirmation">Conferma Email</option>
                    <option value="AccountUnlocked">Account Sbloccato</option>
                    <option value="AccountLocked">Account Bloccato</option>
                    <option value="RegistrationAttempt">Tentativo Registrazione</option>
                    <option value="RegistrationCompleted">Registrazione Completata</option>
                  </select>
                  <label htmlFor="auditFilterAction">Tipo Evento</label>
                </FloatLabel>
              </div>

              <div>
                <FloatLabel>
                  <select
                    id="auditFilterSuccess"
                    value={auditFilterSuccess}
                    onChange={(e) => setAuditFilterSuccess(e.target.value)}
                    style={{ width: '100%', padding: '11px', border: '1px solid #ced4da', borderRadius: '4px' }}
                  >
                    <option value="">Tutti</option>
                    <option value="true">Solo successi</option>
                    <option value="false">Solo fallimenti</option>
                  </select>
                  <label htmlFor="auditFilterSuccess">Esito</label>
                </FloatLabel>
              </div>

              <div>
                <label htmlFor="auditFilterDateFrom" style={{ display: 'block', marginBottom: '5px', fontSize: '0.9em', color: '#666' }}>
                  Data Da
                </label>
                <InputText
                  id="auditFilterDateFrom"
                  type="date"
                  value={auditFilterDateFrom}
                  onChange={(e) => setAuditFilterDateFrom(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label htmlFor="auditFilterDateTo" style={{ display: 'block', marginBottom: '5px', fontSize: '0.9em', color: '#666' }}>
                  Data A
                </label>
                <InputText
                  id="auditFilterDateTo"
                  type="date"
                  value={auditFilterDateTo}
                  onChange={(e) => setAuditFilterDateTo(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <Button
                label="Applica Filtri"
                icon="pi pi-search"
                onClick={handleSearchAuditLogs}
                loading={isLoadingAudit}
              />
              <Button
                label="Reset Filtri"
                icon="pi pi-refresh"
                onClick={handleResetFilters}
                className="p-button-secondary"
                loading={isLoadingAudit}
              />
            </div>
          </div>

          {/* Tabella Log */}
          {isLoadingAudit ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
              <p>Caricamento log in corso...</p>
            </div>
          ) : auditLogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              <i className="pi pi-info-circle" style={{ fontSize: '2rem' }}></i>
              <p>Nessun log trovato per i criteri selezionati</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.9em'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Data/Ora</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Codice Fiscale</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Azione</th>
                    <th style={{ padding: '10px', textAlign: 'center' }}>Esito</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>IP Address</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Dettagli</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log, index) => (
                    <tr
                      key={log.id || index}
                      style={{
                        borderBottom: '1px solid #eee',
                        backgroundColor: log.action === 'AccountLocked' ? '#fff3e0' : (log.success ? 'transparent' : '#ffebee')
                      }}
                    >
                      <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td style={{ padding: '10px', fontFamily: 'monospace' }}>
                        {log.userName}
                      </td>
                      <td style={{ padding: '10px' }}>
                        {getActionLabel(log.action)}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        {log.success ? (
                          <i className="pi pi-check-circle" style={{ color: 'green' }}></i>
                        ) : (
                          <i className="pi pi-times-circle" style={{ color: 'red' }}></i>
                        )}
                      </td>
                      <td style={{ padding: '10px', fontSize: '0.85em' }}>
                        {log.ipAddress || '-'}
                      </td>
                      <td style={{ padding: '10px', fontSize: '0.85em', maxWidth: '300px' }}>
                        {log.failureReason || log.details || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Info */}
          {auditLogs.length > 0 && (
            <div style={{
              marginTop: '20px',
              padding: '10px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
              fontSize: '0.9em'
            }}>
              <i className="pi pi-info-circle" style={{ marginRight: '5px' }}></i>
              Visualizzati {auditLogs.length} eventi degli ultimi 30 giorni
            </div>
          )}

          {/* Info Box Account Bloccati */}
          {auditFilterAction === 'AccountLocked' && (
            <div style={{
              marginTop: '15px',
              padding: '15px',
              backgroundColor: '#fff3e0',
              borderLeft: '4px solid #f57c00',
              borderRadius: '4px',
              fontSize: '0.9em'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#e65100' }}>
                <i className="pi pi-lock" style={{ marginRight: '8px' }}></i>
                Cosa significa "Account Bloccato"?
              </div>
              <ul style={{ margin: '10px 0', paddingLeft: '20px', lineHeight: '1.6' }}>
                <li><strong>Causa:</strong> L'account viene bloccato automaticamente dopo <strong>5 tentativi di login falliti consecutivi</strong></li>
                <li><strong>Durata:</strong> Il blocco dura <strong>15 minuti</strong> e si sblocca automaticamente</li>
                <li><strong>Sblocco manuale:</strong> Gli amministratori possono sbloccare l'account manualmente usando il pulsante "Sblocca Utente"</li>
                <li><strong>Sicurezza:</strong> Questa misura protegge gli account da attacchi brute-force e tentativi di accesso non autorizzati</li>
              </ul>
              <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#fff', borderRadius: '3px', fontSize: '0.85em' }}>
                <i className="pi pi-question-circle" style={{ marginRight: '5px', color: '#f57c00' }}></i>
                <strong>Cosa fare:</strong> Se un utente è bloccato, verifica che stia usando la password corretta.
                Puoi sbloccarlo manualmente o l'utente può attendere 15 minuti per riprovare.
              </div>
            </div>
          )}
        </div>
      </Dialog>

      {/* Dialog Audit Ripubblicazioni */}
      <Dialog
        header="Audit Ripubblicazioni Referti"
        visible={republishAuditVisible}
        style={{ width: "90vw", maxWidth: "1200px" }}
        onHide={() => {
          setRepublishAuditVisible(false);
          setRepublishSearchCF("");
          setRepublishFilterStatus("");
          setRepublishFilterDateFrom("");
          setRepublishFilterDateTo("");
          setRepublishAuditLogs([]);
          setRepublishAuditStats(null);
        }}
        maximizable
      >
        <div className="audit-log-container">
          {/* Statistiche */}
          {republishAuditStats && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '10px',
              marginBottom: '20px'
            }}>
              <Card style={{ padding: '10px', backgroundColor: '#e3f2fd' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>
                    {republishAuditStats.totalRequests || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#555' }}>Richieste Totali</div>
                </div>
              </Card>
              <Card style={{ padding: '10px', backgroundColor: '#e8f5e9' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#388e3c' }}>
                    {republishAuditStats.confirmed || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#555' }}>Confermate</div>
                </div>
              </Card>
              <Card style={{ padding: '10px', backgroundColor: '#fff3e0' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f57c00' }}>
                    {republishAuditStats.pending || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#555' }}>In Attesa</div>
                </div>
              </Card>
              <Card style={{ padding: '10px', backgroundColor: '#ffebee' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#c62828' }}>
                    {republishAuditStats.expired || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#555' }}>Scadute</div>
                </div>
              </Card>
            </div>
          )}

          {/* Filtri */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
              <div>
                <FloatLabel>
                  <InputText
                    id="republishSearchCF"
                    value={republishSearchCF}
                    onChange={(e) => setRepublishSearchCF(e.target.value.toUpperCase())}
                    maxLength={16}
                    style={{ width: '100%' }}
                    onKeyPress={(e) => e.key === "Enter" && handleSearchRepublishAudit()}
                  />
                  <label htmlFor="republishSearchCF">Codice Fiscale</label>
                </FloatLabel>
              </div>

              <div>
                <FloatLabel>
                  <select
                    id="republishFilterStatus"
                    value={republishFilterStatus}
                    onChange={(e) => setRepublishFilterStatus(e.target.value)}
                    style={{ width: '100%', padding: '11px', border: '1px solid #ced4da', borderRadius: '4px' }}
                  >
                    <option value="">Tutti gli stati</option>
                    <option value="confirmed">Confermato</option>
                    <option value="pending">In Attesa</option>
                    <option value="expired">Scaduto</option>
                  </select>
                  <label htmlFor="republishFilterStatus">Stato</label>
                </FloatLabel>
              </div>

              <div>
                <label htmlFor="republishFilterDateFrom" style={{ display: 'block', marginBottom: '5px', fontSize: '0.9em', color: '#666' }}>
                  Data Da
                </label>
                <InputText
                  id="republishFilterDateFrom"
                  type="date"
                  value={republishFilterDateFrom}
                  onChange={(e) => setRepublishFilterDateFrom(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label htmlFor="republishFilterDateTo" style={{ display: 'block', marginBottom: '5px', fontSize: '0.9em', color: '#666' }}>
                  Data A
                </label>
                <InputText
                  id="republishFilterDateTo"
                  type="date"
                  value={republishFilterDateTo}
                  onChange={(e) => setRepublishFilterDateTo(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <Button
                label="Applica Filtri"
                icon="pi pi-search"
                onClick={handleSearchRepublishAudit}
                loading={isLoadingRepublishAudit}
              />
              <Button
                label="Reset Filtri"
                icon="pi pi-refresh"
                onClick={handleResetRepublishFilters}
                className="p-button-secondary"
                loading={isLoadingRepublishAudit}
              />
            </div>
          </div>

          {/* Tabella Log */}
          {isLoadingRepublishAudit ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
              <p>Caricamento audit ripubblicazioni...</p>
            </div>
          ) : republishAuditLogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              <i className="pi pi-info-circle" style={{ fontSize: '2rem' }}></i>
              <p>Nessuna richiesta di ripubblicazione trovata</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.9em'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Data Richiesta</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Codice Fiscale</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Email</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Cod. Accettazione</th>
                    <th style={{ padding: '10px', textAlign: 'center' }}>Stato</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Confermato il</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Scadenza Token</th>
                  </tr>
                </thead>
                <tbody>
                  {republishAuditLogs.map((log, index) => (
                    <tr
                      key={log.id || index}
                      style={{
                        borderBottom: '1px solid #eee',
                        backgroundColor: log.status === 'Scaduto' ? '#ffebee'
                          : log.status === 'In attesa' ? '#fffde7'
                          : 'transparent'
                      }}
                    >
                      <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>
                        {formatTimestamp(log.createdAt)}
                      </td>
                      <td style={{ padding: '10px', fontFamily: 'monospace' }}>
                        {log.userName}
                      </td>
                      <td style={{ padding: '10px', fontSize: '0.85em' }}>
                        {log.email}
                      </td>
                      <td style={{ padding: '10px', fontFamily: 'monospace' }}>
                        {log.examinationCode}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        {getRepublishStatusBadge(log.status)}
                      </td>
                      <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>
                        {log.usedAt ? formatTimestamp(log.usedAt) : '-'}
                      </td>
                      <td style={{ padding: '10px', whiteSpace: 'nowrap', fontSize: '0.85em' }}>
                        {formatTimestamp(log.expiresAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Info */}
          {republishAuditLogs.length > 0 && (
            <div style={{
              marginTop: '20px',
              padding: '10px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
              fontSize: '0.9em'
            }}>
              <i className="pi pi-info-circle" style={{ marginRight: '5px' }}></i>
              Visualizzate {republishAuditLogs.length} richieste degli ultimi 90 giorni
            </div>
          )}
        </div>
      </Dialog>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Account;